import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/user-routes"
import { usePhoneCountries } from "../../../../hooks/usePhoneCountries"
import { navActions } from "../../../../Redux/nav/nav.slice"
import ConfigureSummary from "./components/ConfigureSummary/ConfigureSummary"
import Footer from "./components/Footer"
import LeftRail from "./components/LeftRail/LeftRail"
import PreviewModal from "./components/PreviewModal/PreviewModal"
import SectionForm from "./components/SectionForm"
import {
  selectActiveBuildingBlocks,
  selectActiveProcessor,
  selectConnectedProcessors,
  selectFinishedProcessors,
  selectPrefillCollecting,
  setCurrentSection
} from "./redux/configure.slice"
import { getEnrichmentTools, refreshPrefill } from "./redux/configure.thunks"
import { getSectionKeys, SECTION_KEY_ALIASES } from "./schema/sections"
import { allStepsComplete } from "./utils/sectionValidation"

import styles from "./configure.module.less"

// The prefill collector reads a processor in the background after Configure opens, so the
// first profile answer usually lands before its readings do. Ask again while it says it is
// still collecting, for about a minute: the history sample is the slow pass, and a reading
// that has not arrived by then is not worth holding a poll open for.
const PREFILL_POLL_MS = 4000
const PREFILL_POLL_LIMIT = 15

const ConfigurePage = () => {
  const dispatch = useDispatch()
  const sideBarOpen = useSelector((s) => s.nav?.isShowSideBar)
  const setSideBar = useCallback(
    (value) => dispatch(navActions.navSetProp({ key: "isShowSideBar", value })),
    [dispatch]
  )
  const { section: sectionParam } = useParams()
  const navigate = useNavigate()
  const routerPathname = useLocation().pathname
  // The step comes from the URL itself, not only from useParams. Navigation here flows
  // through Redux (redux-first-history) and this component is rendered by BOTH the
  // /recover/configure and /recover/configure/:section routes, so the param could stay
  // undefined after a redirect while the address bar already showed the new step. The
  // component then re-rendered its redirect forever ("Maximum update depth exceeded").
  const currentPath = typeof window === "undefined" ? routerPathname : window.location.pathname
  const CONFIGURE_ROOT = `${RoutesPaths.recover.configure}/`
  const sectionFromPath = currentPath.startsWith(CONFIGURE_ROOT)
    ? currentPath.slice(CONFIGURE_ROOT.length).split("/")[0] || undefined
    : undefined
  const section = sectionParam || sectionFromPath
  const [previewOpen, setPreviewOpen] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  // Ensures the phone-countries cache (flags + dial codes) is loaded so the
  // PhoneInput in Business Profile renders flags.
  usePhoneCountries()
  // The form pane scrolls independently of the page; hold a ref so each step
  // change can reset it to the top (see the validSection effect below).
  const scrollRef = useRef(null)
  // Shell ref: the wizard height is derived from the element's real top offset
  // (below whatever header the current mode renders) rather than a hardcoded
  // header height, so the footer always pins to the bottom with no empty band.
  const shellRef = useRef(null)

  const buildingBlocks = useSelector(selectActiveBuildingBlocks)
  const connectedProcessors = useSelector(selectConnectedProcessors)
  const activeProcessor = useSelector(selectActiveProcessor)
  // Setup is "finished" once the Finish marker is set OR every step passes its gate
  // (allStepsComplete, the same bar as Next/Finish and the rail's green checks, rather
  // than every optional field being answered), EXCEPT
  // while a per-processor finish is in progress (the first of several processors is done
  // but others remain). Finishing lands on a dedicated Summary step (see below), not a
  // reused step-1 route.
  const finishedProcessors = useSelector(selectFinishedProcessors)
  const midPerProcessorFinish =
    finishedProcessors.length > 0 &&
    !connectedProcessors.every((s) => finishedProcessors.includes(s))
  const profileFinished =
    (connectedProcessors.length > 0 &&
      connectedProcessors.every((s) => finishedProcessors.includes(s))) ||
    (allStepsComplete(buildingBlocks) && !midPerProcessorFinish)
  // The summary is PER PROCESSOR: the active processor gets its summary once IT is
  // finished, while a not-yet-finished processor keeps its live wizard even when
  // another one is already done (and even when Recover is already active for it).
  const activeSlug = activeProcessor || connectedProcessors[0]
  const activeProcFinished = profileFinished || finishedProcessors.includes(activeSlug)
  // The review-and-activate summary is its own step at /recover/configure/summary, shown
  // only once the ACTIVE processor's setup is finished. Everything else is a wizard step.
  const isSummary = section === "summary"

  // Collapse the main app nav while configuring (the wizard's rail + form pane
  // need the room); restore it to its previous state on exit. Mirrors Deflect Configure.
  const prevSideBarRef = useRef(sideBarOpen)
  useEffect(() => {
    prevSideBarRef.current = sideBarOpen
    setSideBar(false)
    return () => setSideBar(prevSideBarRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Unsaved-edits guard. The reducer flags a section dirty on edit and clears it
  // on save (ui.dirtySections). If anything is unsaved, warn before the page is
  // closed/refreshed/navigated away so the merchant doesn't lose their answers.
  // (In-app route changes keep the edits in Redux, so only a full unload loses
  // them; react-router 6.3 has no useBlocker for in-app blocking anyway.)
  const hasUnsavedEdits = useSelector((s) =>
    Object.values(s.disputes?.ui?.dirtySections || {}).some(Boolean)
  )
  useEffect(() => {
    if (!hasUnsavedEdits) return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [hasUnsavedEdits])

  useEffect(() => {
    document.title = "Configure - Chargeback"
  }, [])

  // Loaded here rather than on the Enrichment step: the rail and the summary read every
  // processor's connected enrichment tools from this catalog.
  useEffect(() => {
    dispatch(getEnrichmentTools())
  }, [dispatch])

  const prefillCollecting = useSelector(selectPrefillCollecting)
  useEffect(() => {
    if (!prefillCollecting) return undefined
    let polls = 0
    const timer = setInterval(() => {
      polls += 1
      dispatch(refreshPrefill())
      if (polls >= PREFILL_POLL_LIMIT) clearInterval(timer)
    }, PREFILL_POLL_MS)
    return () => clearInterval(timer)
  }, [prefillCollecting, dispatch])

  // The active step list: the experimental 5-step set when in experimental mode, else
  // today's 7 sections. A valid step is resolved FIRST, so a key that doubles as a
  // legacy alias (e.g. business_profile) is treated as a real step in experimental mode
  // and still redirects in normal mode (where it isn't a valid step).
  const sectionKeys = getSectionKeys()
  const validSection = sectionKeys.includes(section) ? section : null
  // Map any legacy section key (business_profile / business_policies / etc.) to its new
  // equivalent so old links keep working, but only when it isn't already a valid step.
  const aliasTarget = !validSection && section && SECTION_KEY_ALIASES[section]

  useEffect(() => {
    if (validSection) dispatch(setCurrentSection(validSection))
    setShowErrors(false)
  }, [validSection, dispatch])

  // On step change, return the form pane (and the page on mobile) to the top so
  // each step opens from its start rather than inheriting the previous step's
  // scroll position. useLayoutEffect resets before paint to avoid a visible jump.
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    document.getElementById("main-content")?.scrollTo(0, 0)
  }, [section])

  // Size the wizard to the space actually left below the header, measured from the
  // shell's own top rather than assuming a fixed 64px header (which varies per mode).
  // Keeps the footer pinned to the viewport bottom with no empty band. Recomputes on
  // resize; the CSS calc(100vh - 64px) stays as the pre-measure fallback.
  useLayoutEffect(() => {
    const el = shellRef.current
    if (!el) return undefined
    const setHeight = () => {
      const top = el.getBoundingClientRect().top
      el.style.height = `${Math.max(0, window.innerHeight - top)}px`
    }
    setHeight()
    window.addEventListener("resize", setHeight)
    return () => window.removeEventListener("resize", setHeight)
  }, [section])

  // Where this URL should really land:
  //   - a legacy section key redirects to its new equivalent, so old links keep working;
  //   - Summary is only a real destination once the ACTIVE processor's setup is finished
  //     (before then keep building at step 1), EXCEPT when Summary is itself a listed
  //     step (Preview & Confirm), where the rail's completeness gate governs it;
  //   - bare /recover/configure or an unknown section: a finished active processor lands
  //     on its Summary, everyone else on step 1.
  // null once the URL is already correct. The redirect runs in an effect and is skipped
  // when we are already at the target, so it can never re-fire against itself.
  let redirectTarget = null
  if (aliasTarget) redirectTarget = RoutesPaths.recover.configure_section(aliasTarget)
  else if (isSummary && !activeProcFinished && !sectionKeys.includes("summary"))
    redirectTarget = RoutesPaths.recover.configure_section(sectionKeys[0])
  else if (!validSection && !isSummary)
    redirectTarget = RoutesPaths.recover.configure_section(
      activeProcFinished ? "summary" : sectionKeys[0]
    )
  const needsRedirect = !!redirectTarget && redirectTarget !== currentPath
  useEffect(() => {
    if (needsRedirect) navigate(redirectTarget, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRedirect, redirectTarget])

  if (needsRedirect) return null

  return (
    <div className={styles.configure} ref={shellRef}>
      <LeftRail
        currentSection={isSummary ? "summary" : validSection}
        profileFinished={activeProcFinished}
        onOpenPreview={() => setPreviewOpen(true)}
      />
      <div className={styles.configureMain}>
        <div className={styles.configureMainScroll} ref={scrollRef}>
          {isSummary ? (
            <ConfigureSummary />
          ) : (
            <SectionForm sectionKey={validSection} showErrors={showErrors} />
          )}
        </div>
        {isSummary ? null : (
          <Footer currentSection={validSection} onRequestNext={(ok) => setShowErrors(!ok)} />
        )}
      </div>
      <PreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
  )
}

export default ConfigurePage
